import {
  chain,
  filter,
  find,
  first,
  flatMap,
  forEach,
  get,
  has,
  isEmpty,
  map,
  reduce,
  tail,
  toNumber,
  toString,
  toUpper,
  assign,
  omit,
  some,
  isNil,
  cloneDeep,
  isUndefined,
  trim,
  eq
} from 'lodash';
import { ReduxStore } from './payload-types';
import { COVERME_STUDENT_TYPES, COVERME_TRAVELLER_TYPES, QuoteForm, QuoteFormTraveller } from './quote-form';
import { RecommendationPayload, RecommendationResponsePayload } from './recommendation';
import { PaxMQState } from './mq';
import { PlanParams, PRODUCT_TYPE, TravellerParams } from './travellers-plan';
import { Dependent, Pax, Questionnaire, QuoteRequestPayload, TripInformation, TripType } from './quote';
import { PolicyRequest, PolicyPax } from './policy-create';
import { TravellerDetails, TravellerDetailsPayload } from './travellers-details';
import { PAYMENT_METHOD, PAYMENT_TYPE } from './traveller-payments';
import {
  PAYMENT_METHOD_MAPPING,
  API_KEYS,
  API_DATE_FORMAT,
  AUTH_HEADERS,
  QUOTE_BLANK,
  POLICY_PERMISSIONS,
  ORG_CATEGORY_SPONSOR,
  ORG_CATEGORY_BROKER,
  COUNTRY_CODES,
  FEATURE_FLAGS
} from '@trip-ux/constants';
import { checkTrue, dayjs, checkIfCoverMeOrManulifeEmployee } from '@trip-ux/common';
import { ConfigState } from './config';
import { TMSData } from './tms';
import { BenefitTypes } from './global';
import { add, BigNumber, bignumber } from 'mathjs';
import { PolicyInfoList } from './policy-info';
import { NAState, NAQuestionCategories, NAQuestionsPerCategory } from './na';
import { additionalInfo, checkFeatureFlag, getManulifeEmployeeRelationShip } from './utils';
const TWO = 2;
const THREE = 3;

const mapTripParams = (params: PlanParams, organizationCode?: string) => {
  if (!checkIfCoverMeOrManulifeEmployee(organizationCode)) {
    omit(params, 'tripCost');
    omit(params, 'coverageAmount');
  }
  const { deductible, daysPerTrip, tripCost, ...rest } = params;
  let response = {
    ...rest,
    ...(!isNil(daysPerTrip) && { daysPerTrip: toNumber(daysPerTrip) }),
    adjustments: !isUndefined(deductible)
      ? [
          {
            type: API_KEYS.DEDUCTIBLE,
            value: toNumber(deductible)
          }
        ]
      : []
  };
  if (checkIfCoverMeOrManulifeEmployee(organizationCode)) {
    response = {
      ...response,
      ...(!isEmpty(tripCost) && { tripCost: toNumber(tripCost) }),
      ...(!isEmpty(params.coverageAmount) && { coverageAmount: toNumber(params.coverageAmount) })
    };
  }
  return response;
};

const primaryTravellersFromPolicies = (input: PolicyInfoList, dateFormat: string) => {
  return map(input.selectedPolicies, p => {
    const { policyNumber, dateOfBirth, referenceNumber, tripBookingDate, age } = first(p.paxes);
    const tripCost = get(input, `editChanges[${policyNumber}].tripCost`);
    const dob = get(input, `editChanges[${policyNumber}].dateOfBirth`, dateOfBirth);
    const ageEdit = get(input, `editChanges[${policyNumber}].age`, age);
    return {
      referenceNumber,
      age: toNumber(ageEdit),
      tripCost,
      dob: getDependentDateOfBirth(dob, dateFormat),
      tripBookingDate
    };
  });
};

const travellersToDependents = (travellers, referenceNumber, dateFormat?) => {
  return map(
    travellers,
    traveller =>
      ({
        referenceNumber: toString(referenceNumber),
        age: toNumber(traveller.age),
        tripCost: toNumber(traveller.tripCost) || 0,
        ...(dateFormat ? { dateOfBirth: getDependentDateOfBirth(traveller.dateOfBirth, dateFormat) } : {})
      } as Dependent)
  );
};

const travellersToPaxes = (travellers, dateFormat) => {
  return map(travellers, traveller => ({
    referenceNumber: toString(traveller.referenceNumber),
    dateOfBirth: dayjs(traveller.dob, dateFormat).format(API_DATE_FORMAT)
  }));
};

const getTravellerCategoryFromPlanCode = (planCode?: string) => {
  if (planCode?.includes('SS')) {
    return COVERME_TRAVELLER_TYPES.STUDENT;
  }
  if (planCode?.includes('TU')) {
    return COVERME_TRAVELLER_TYPES.EXISTING_PLAN;
  }
  if (planCode?.includes('SV')) {
    return COVERME_TRAVELLER_TYPES.VISITOR_TO_CANADA;
  }
  return COVERME_TRAVELLER_TYPES.CANADIAN_CITIZEN;
};

const getTravellerCategoryFromPolicy = (policy: PolicyRequest) =>
  get(policy, 'travellerCategory') || getTravellerCategoryFromPlanCode(get(policy, 'paxes[0].plan.code'));

export const getLabels = (config: ConfigState, tms: TMSData, translationField = 'translation') =>
  get(
    find(tms.labels, {
      translation: get(config, `options.${translationField}`),
      translationSpace: get(config, 'options.translationSpace')
    }),
    'data'
  );

const getConfigDateFormat = labels => get(labels, 'configuration.apiDateFormat');

export const prepareIndividual = (payload: { paxes: { referenceNumber: string }[] }, paxId: number) => ({
  ...payload,
  paxes: filter(payload.paxes, ({ referenceNumber }) => referenceNumber === toString(paxId))
});

export const prepareRecommendation = (store: ReduxStore): RecommendationPayload => {
  const { quoteForm, tms, config, userProfile } = store;
  const labels = getLabels(config, tms, 'translationConfig');
  return mapQuoteFormToRecommendationPayload(quoteForm.form, {
    dateFormat: getConfigDateFormat(labels),
    language: toUpper(config.locale),
    //TODO needed to be synced with entitlement
    agentKey: API_KEYS.STUB_AGENT_KEY,
    useDestinationPricing: checkFeatureFlag(userProfile?.featureFlags, FEATURE_FLAGS.DESTINATION_PRICING)
  });
};

export const prepareRecommendationForPolicyInfo = (store: ReduxStore): RecommendationPayload => {
  const { policyInfo, tms, config, userProfile } = store;
  const labels = getLabels(config, tms);
  return mapPolicyInfoToRecommendationPayload(policyInfo, {
    dateFormat: labels.endorse_policy.rider.dateFormat,
    language: toUpper(config.locale),
    //TODO needed to be synced with entitlement
    agentKey: API_KEYS.STUB_AGENT_KEY,
    useDestinationPricing: checkFeatureFlag(userProfile?.featureFlags, FEATURE_FLAGS.DESTINATION_PRICING)
  });
};

export const getDefaultCountryOfResidence = (travellerType, studentType?, country?) => {
  const isNonResidentOfCanada = !isEmpty(country) && country !== API_KEYS.CANADA;
  const isVisitorToCanada = travellerType === COVERME_TRAVELLER_TYPES.VISITOR_TO_CANADA;
  const isInternationalStudent = studentType === COVERME_STUDENT_TYPES.INTERNATIONAL;
  return isVisitorToCanada || isInternationalStudent || isNonResidentOfCanada
    ? API_KEYS.INTERNATIONAL
    : API_KEYS.CANADA;
};

const getTripStartDate = (travellerType, studentType, arrivingDate, dateFormat) => {
  const isVisitorToCanada = travellerType === COVERME_TRAVELLER_TYPES.VISITOR_TO_CANADA;
  const isInternationalStudent = studentType === COVERME_STUDENT_TYPES.INTERNATIONAL;
  return (isVisitorToCanada || isInternationalStudent) && !isEmpty(arrivingDate)
    ? dayjs(arrivingDate, dateFormat).format(API_DATE_FORMAT)
    : undefined;
};

const getIsSuperVisa = (travellerType, isSuperVisa) => {
  const isVisitorToCanada = travellerType === COVERME_TRAVELLER_TYPES.VISITOR_TO_CANADA;
  return isVisitorToCanada ? checkTrue(isSuperVisa) : undefined;
};

export const prepareTripInformation = (input: QuoteForm, dateFormat: string): TripInformation => {
  const tripType =
    checkTrue(input.multiTripTemp) || checkTrue(input.multiTrip)
      ? (API_KEYS.MULTIPLE as TripType)
      : (API_KEYS.SINGLE as TripType);

  const returnDate = tripType === API_KEYS.MULTIPLE ? input.returnDateMultiTrip : input.returnDate;
  const endDate = dayjs(returnDate, dateFormat).format(API_DATE_FORMAT);

  let travellingWithinCanada = !checkTrue(input.travelOutsideCanada);
  if (input.travellerType === COVERME_TRAVELLER_TYPES.STUDENT) {
    travellingWithinCanada = input.studentType !== COVERME_STUDENT_TYPES.CANADIAN_OUTSIDE;
  }

  return {
    startDate: dayjs(input.departureDate, dateFormat).format(API_DATE_FORMAT),
    effectiveDate: dayjs(input.departureDate, dateFormat).format(API_DATE_FORMAT),
    tripStartDate: getTripStartDate(input.travellerType, input.studentType, input.arrivingDate, dateFormat),
    endDate,
    existingCoverageDays: input.planCoverageDayCount,
    destinationCountry: input.destinationCountry,
    destinationName: input.destinationName,
    tripType,
    provinceOfResidence: input.province,
    travellingWithinCanada,
    countryOfResidence: getDefaultCountryOfResidence(input.travellerType, input.studentType),
    ...schoolDetails(input, dateFormat),
    isSuperVisa: getIsSuperVisa(input.travellerType, input.superVisa),
    ...(input.destinationCountry &&
      input.destinationCountry !== COUNTRY_CODES.US &&
      !isEmpty(input.hasUSLayover) && { hasUSLayover: checkTrue(input.hasUSLayover) }),
    ...destination(input)
  };
};

const prepareQuoteTripInformation = (input: QuoteForm, dateFormat: string, useDestinationPricing: boolean) => {
  const tripInformation = prepareTripInformation(input, dateFormat);
  if (!useDestinationPricing) {
    return omit(tripInformation, 'hasUSLayover');
  }
  const destinationPricingTripInformation = omit(tripInformation, 'destinationName');
  if (!destinationPricingTripInformation.travellingWithinCanada) {
    return destinationPricingTripInformation;
  }
  if (destinationPricingTripInformation.destinationCountry) {
    return destinationPricingTripInformation;
  }
  return { ...destinationPricingTripInformation, destinationCountry: COUNTRY_CODES.CA };
};

const mapFurthestDestination = (furthestDestination: string): object => {
  const newValue = furthestDestination.split(',');
  const completeInfo = newValue.length === THREE;
  const noIata = newValue.length === TWO;

  if (completeInfo) {
    return { iata: trim(newValue[0]), airportName: trim(newValue[1]), country: trim(newValue[TWO]) };
  } else if (noIata) {
    return { airportName: trim(newValue[0]), country: trim(newValue[1]) };
  } else {
    return { country: trim(newValue[0]) };
  }
};

const destination = (input: QuoteForm) => {
  return {
    ...(!isEmpty(input.departureLocation) && { departureLocation: input.departureLocation }),
    ...(!isEmpty(input.furthestDestination) && {
      furthestDestination: mapFurthestDestination(input.furthestDestination)
    })
  };
};

const schoolDetails = (input: QuoteForm, dateFormat: string) => {
  return {
    ...(!isEmpty(input.schoolStartDate) && {
      educationStartDate: dayjs(input.schoolStartDate, dateFormat).format(API_DATE_FORMAT)
    }),
    ...(!isEmpty(input.schoolEndDate) && {
      educationEndDate: dayjs(input.schoolEndDate, dateFormat).format(API_DATE_FORMAT)
    }),
    ...(!isEmpty(input.leavingDate) && { leavingDate: dayjs(input.leavingDate, dateFormat).format(API_DATE_FORMAT) }),
    ...(!isEmpty(input.schoolCity) && { schoolCity: input.schoolCity }),
    ...(!isEmpty(input.schoolName) && { schoolName: input.schoolName })
  };
};

const prepareTripInformationForPolicyInfo = (input: TripInformation, dateFormat: string) => ({
  startDate: dayjs(input.startDate, dateFormat).format(API_DATE_FORMAT),
  effectiveDate: dayjs(input.startDate, dateFormat).format(API_DATE_FORMAT),
  endDate: dayjs(input.endDate, dateFormat).format(API_DATE_FORMAT),
  tripType: input.tripType,
  provinceOfResidence: input.provinceOfResidence,
  travellingWithinCanada: input.travellingWithinCanada,
  countryOfResidence: getDefaultCountryOfResidence(null, null, input.countryOfResidence),
  existingCoverageDays: input.existingCoverageDays
});

const getHasUSLayoverEmit = (input: TripInformation, destinationCountry?: string) =>
  destinationCountry && destinationCountry !== COUNTRY_CODES.US && typeof input.hasUSLayover === 'boolean'
    ? { hasUSLayover: input.hasUSLayover }
    : {};

const getDestinationPricingTripInformation = (
  base: ReturnType<typeof prepareTripInformationForPolicyInfo>,
  input: TripInformation
) => {
  if (input.travellingWithinCanada) {
    if (!input.destinationCountry) {
      return { ...base, destinationCountry: COUNTRY_CODES.CA };
    }
    return {
      ...base,
      destinationCountry: input.destinationCountry,
      ...getHasUSLayoverEmit(input, input.destinationCountry)
    };
  }

  const destinationCountry = input.destinationCountry || get(input, 'furthestDestination.country');
  return {
    ...base,
    ...(destinationCountry && { destinationCountry }),
    ...getHasUSLayoverEmit(input, destinationCountry)
  };
};

const prepareQuoteTripInformationForPolicyInfo = (
  input: TripInformation,
  dateFormat: string,
  useDestinationPricing: boolean
) => {
  const base = prepareTripInformationForPolicyInfo(input, dateFormat);
  if (!useDestinationPricing) {
    return base;
  }
  return getDestinationPricingTripInformation(base, input);
};

export const mapQuoteFormToRecommendationPayload = (
  input: QuoteForm,
  options: {
    dateFormat: string; //APIDATEFORMAT DD/MM/YYYY
    agentKey: string;
    language: string;
    useDestinationPricing?: boolean;
  }
): RecommendationPayload => {
  const { dateFormat, language, agentKey, useDestinationPricing = false } = options;
  const isFamily = checkTrue(input.familyPlans);
  const payload = {
    language,
    agentKey,
    applicationDate: dayjs(get(input, 'applicationDate', dayjs()), dateFormat).format(),
    // No plan is selected yet at the recommendation stage, so there is no reliable
    // secondary signal to derive a category from if travellerType is missing.
    travellerCategory: input.travellerType,
    travellerType: isFamily ? API_KEYS.FAMILY : API_KEYS.SINGLE,
    paxes: [],
    dependents: [],
    tripInformation: prepareQuoteTripInformation(input, dateFormat, useDestinationPricing)
  } as RecommendationPayload;
  const applicationDate = get(input, 'applicationDate', dayjs())
    ? dayjs(get(input, 'applicationDate', dayjs()), dateFormat).format(API_DATE_FORMAT)
    : dayjs().format(API_DATE_FORMAT);
  if (isFamily) {
    const { referenceNumber, tripBookingDate, dateOfBirth } = first(input.travellers);
    payload.paxes.push({
      referenceNumber: toString(referenceNumber),
      dateOfBirth: dayjs(dateOfBirth, dateFormat).format(API_DATE_FORMAT),
      tripBookingDate: tripBookingDate ? dayjs(tripBookingDate, dateFormat).format(API_DATE_FORMAT) : applicationDate
    });
    forEach(tail(input.travellers), traveller =>
      payload.dependents.push({
        referenceNumber: toString(referenceNumber),
        dateOfBirth: dayjs(traveller.dateOfBirth, dateFormat).format(API_DATE_FORMAT),
        tripCost: toNumber(traveller.tripCost) || 0
      })
    );
  } else {
    delete payload.dependents;
    forEach(input.travellers, traveller =>
      payload.paxes.push({
        referenceNumber: toString(traveller.referenceNumber),
        dateOfBirth: dayjs(traveller.dateOfBirth, dateFormat).format(API_DATE_FORMAT),
        tripBookingDate: traveller.tripBookingDate
          ? dayjs(traveller.tripBookingDate, dateFormat).format(API_DATE_FORMAT)
          : applicationDate
      })
    );
  }
  return payload;
};

export const mapPolicyInfoToRecommendationPayload = (
  input: PolicyInfoList,
  options: {
    dateFormat: string;
    agentKey: string;
    language: string;
    useDestinationPricing?: boolean;
  }
): RecommendationPayload => {
  const { dateFormat, language, agentKey, useDestinationPricing = false } = options;
  const policies = input.selectedPolicies;
  const travellerType = get(first(policies), 'travellerType');
  const isFamily = travellerType === 'FAMILY';
  const applicationDate = first(policies).applicationDate;
  const createdAt = first(policies).createdAt;
  const tripInformation = assign(
    get(cloneDeep(first(policies)), 'tripInformation'),
    get(input, 'editChanges.tripInformation')
  );
  const primaryTravellers = primaryTravellersFromPolicies(input, dateFormat);
  const payload = {
    language,
    agentKey,
    applicationDate: dayjs(applicationDate).format(),
    createdAt: dayjs(createdAt).format(),
    travellerType: isFamily ? API_KEYS.FAMILY : API_KEYS.SINGLE,
    travellerCategory: getTravellerCategoryFromPolicy(first(policies)),
    paxes: [],
    dependents: [],
    tripInformation: prepareQuoteTripInformationForPolicyInfo(tripInformation, dateFormat, useDestinationPricing)
  } as RecommendationPayload;
  if (isFamily) {
    const { referenceNumber, dob } = first(primaryTravellers);
    payload.paxes.push({
      referenceNumber: toString(referenceNumber),
      dateOfBirth: dayjs(dob, dateFormat).format(API_DATE_FORMAT)
    });
    payload.dependents.push(...travellersToDependents(input.dependentsChanges, referenceNumber));
  } else {
    delete payload.dependents;
    payload.paxes.push(...travellersToPaxes(primaryTravellers, dateFormat));
  }
  return payload;
};

export const processMQ = (mq: PaxMQState[]): Questionnaire => ({
  type: API_KEYS.MEDICAL,
  answers: reduce(flatMap(mq, 'questions'), (res, val) => [...res, ...map(val, (answer, key) => ({ answer, key }))], [])
});

export const processNA = (na: NAState, travellerNumber: number): Questionnaire => {
  const PRIMARY_TRAVELLER_INDEX = 1;
  const QUESTIONNAIRE_INDEX = 0;
  const processedNA = { type: API_KEYS.NEEDS_ANALYSIS, answers: [] };
  for (const category of NAQuestionCategories) {
    let travellerNAForm = na[travellerNumber][QUESTIONNAIRE_INDEX];
    if (travellerNumber > 1 && checkTrue(travellerNAForm.questions[category])) {
      travellerNAForm = na[PRIMARY_TRAVELLER_INDEX][QUESTIONNAIRE_INDEX];
    }
    for (const question of NAQuestionsPerCategory[category]) {
      processedNA.answers.push({
        answer: get(travellerNAForm.questions, question),
        key: question
      });
    }
  }
  return processedNA;
};
const tripCostIfAgency = (organizationCategory: string, tripCost: number) => {
  return organizationCategory !== ORG_CATEGORY_SPONSOR && organizationCategory !== ORG_CATEGORY_BROKER
    ? { tripCost }
    : {};
};
export const processRecommendation = (
  paxRecommendation: RecommendationResponsePayload,
  params: TravellerParams[],
  options: {
    referenceNumber: number;
    tripCost: number;
    organizationCode: string;
    organizationCategory: string;
    medicalCoverage?: string;
    travellerType?: string;
    isMaintenance?: boolean;
  }
) => {
  if (!paxRecommendation) {
    console.warn(
      `[processRecommendation] paxRecommendation is undefined for pax ${options.referenceNumber} — recommendation data missing`
    );
    return [];
  }
  return map(get(paxRecommendation, 'plans', []), rec => {
    const {
      referenceNumber,
      tripCost,
      organizationCode,
      medicalCoverage,
      travellerType,
      isMaintenance,
      organizationCategory
    } = options;
    const planParamsFilter = isMaintenance ? { code: rec.code } : { paxId: referenceNumber, plan: rec.code };
    const planParamsKey = isMaintenance ? 'tripPreference' : 'params';
    const { adjustments, ...rest } = mapTripParams(
      chain(params).find(planParamsFilter).get(`${planParamsKey}`, {}).value(),
      organizationCode
    );
    return {
      code: rec.code,
      tripPreference: {
        tripCost: toNumber(tripCost) || 0,
        ...(travellerType === COVERME_TRAVELLER_TYPES.VISITOR_TO_CANADA && {
          coverageAmount: toNumber(medicalCoverage)
        }),
        ...rest,
        ...tripCostIfAgency(organizationCategory, toNumber(tripCost) || 0)
      },
      adjustments,
      optionalBenefits: map(
        filter(
          rec.benefits,
          ben => ben.benefitType !== BenefitTypes.MANDATORY && (isMaintenance || ben.purchasable !== false)
        ),
        ({ code }) => ({ code })
      )
    };
  });
};

export const mapQuoteFormToPayload = (
  input: QuoteForm,
  recommendation: RecommendationResponsePayload[],
  options: {
    dateFormat: string;
    agentKey: string;
    language: string;
    organizationCode: string;
    organizationCategory: string;
    useDestinationPricing?: boolean;
  },
  travellerPreference: {
    mq?: Record<number, PaxMQState[]>;
    params?: TravellerParams[];
  } = {}
): QuoteRequestPayload => {
  const { dateFormat, language, organizationCode, organizationCategory, useDestinationPricing = false } = options;
  const { mq = {}, params = [] } = travellerPreference;
  const isFamily = checkTrue(input.familyPlans);
  const payload = {
    language,
    manulifeEmployeeRelationship: getManulifeEmployeeRelationShip(get(input, 'employeeType')),
    applicationDate: dayjs(get(input, 'applicationDate', dayjs()), dateFormat).format(),
    travellerType: isFamily ? API_KEYS.FAMILY : API_KEYS.SINGLE,
    travellerCategory:
      input.travellerType ||
      (useDestinationPricing &&
        getTravellerCategoryFromPlanCode(
          get(find(params, { paxId: get(first(input.travellers), 'referenceNumber') }), 'plan')
        )),
    paxes: [],
    dependents: [],
    tripInformation: prepareQuoteTripInformation(input, dateFormat, useDestinationPricing)
  } as QuoteRequestPayload;
  let paramOptions;

  if (isFamily) {
    const { age, tripCost, referenceNumber, dateOfBirth } = first(input.travellers);
    const { medicalCoverage, travellerType } = input;
    const paxRecommendation = find(recommendation, {
      referenceNumber: toString(referenceNumber)
    });
    paramOptions = {
      referenceNumber,
      tripCost,
      medicalCoverage,
      travellerType,
      organizationCode,
      organizationCategory,
      isMaintenance: false
    };

    payload.paxes.push({
      referenceNumber: toString(referenceNumber),
      dateOfBirth: dayjs(dateOfBirth, dateFormat).format(API_DATE_FORMAT),
      age: toNumber(age),
      tripBookingDate: dayjs(get(input, 'applicationDate', dayjs()), dateFormat).format(),
      plans: processRecommendation(paxRecommendation, params, paramOptions),
      ...(mq && has(mq, referenceNumber) && { questionnaires: [processMQ(mq[referenceNumber])] })
    } as Pax);
    forEach(tail(input.travellers), traveller =>
      payload.dependents.push({
        referenceNumber: toString(traveller.referenceNumber),
        dateOfBirth: getDependentDateOfBirth(traveller.dateOfBirth, dateFormat),
        age: toNumber(traveller.age),
        tripCost: toNumber(traveller.tripCost) || 0
      } as Dependent)
    );
  } else {
    delete payload.dependents;
    forEach(input.travellers, traveller => {
      const { referenceNumber, age, dateOfBirth, tripCost } = traveller;
      const { medicalCoverage, travellerType } = input;
      const paxRecommendation = find(recommendation, {
        referenceNumber: toString(referenceNumber)
      });
      paramOptions = {
        referenceNumber,
        tripCost,
        organizationCode,
        organizationCategory,
        medicalCoverage,
        travellerType,
        isMaintenance: false
      };
      return payload.paxes.push({
        referenceNumber: toString(referenceNumber),
        age: toNumber(age),
        dateOfBirth: getDependentDateOfBirth(dateOfBirth, dateFormat),
        tripBookingDate: dayjs(get(input, 'applicationDate', dayjs()), dateFormat).format(),
        plans: processRecommendation(paxRecommendation, params, paramOptions),
        ...(mq && has(mq, referenceNumber) && { questionnaires: [processMQ(mq[referenceNumber])] })
      } as Pax);
    });
  }
  return payload;
};

export const mapPolicyInfoToQuotePayload = (
  input: PolicyInfoList,
  recommendation: RecommendationResponsePayload[],
  options: {
    dateFormat: string;
    agentKey: string;
    language: string;
    organizationCode: string;
    organizationCategory: string;
    useDestinationPricing?: boolean;
  },
  travellerPreference: {
    mq?: Questionnaire;
    params?: TravellerParams[];
  } = {}
): QuoteRequestPayload => {
  const { dateFormat, language, organizationCode, organizationCategory, useDestinationPricing = false } = options;
  const { mq = {}, params = [] } = travellerPreference;
  const policies = input.selectedPolicies;
  const travellerType = get(first(policies), 'travellerType');
  const isFamily = travellerType === 'FAMILY';
  const applicationDate = first(policies).applicationDate;
  const createdAt = first(policies).createdAt;
  const tripInformation = assign(
    get(cloneDeep(first(policies)), 'tripInformation'),
    get(input, 'editChanges.tripInformation')
  );
  const primaryTravellers = primaryTravellersFromPolicies(input, dateFormat);

  const hasCompanionDiscount = input.selectedPolicies[0].paxes[0].plan.adjustments.find(
    adj => adj.breakdownType === 'COMPANION'
  );

  const payload = {
    language,
    applicationDate: dayjs(applicationDate).format(),
    createdAt: dayjs(createdAt).format(),
    travellerType: isFamily ? API_KEYS.FAMILY : API_KEYS.SINGLE,
    ...(useDestinationPricing && { travellerCategory: getTravellerCategoryFromPolicy(first(policies)) }),
    paxes: [],
    dependents: [],
    tripInformation: prepareQuoteTripInformationForPolicyInfo(tripInformation, dateFormat, useDestinationPricing),
    hasCompanionDiscount: !!hasCompanionDiscount
  } as QuoteRequestPayload;

  let paramOptions;

  if (isFamily) {
    const { age, tripCost, referenceNumber, tripBookingDate, dob } = first(primaryTravellers);
    const paxRecommendation = find(recommendation, {
      referenceNumber
    });
    paramOptions = {
      organizationCode,
      organizationCategory,
      isMaintenance: true,
      tripCost,
      referenceNumber: toNumber(referenceNumber)
    };

    payload.paxes.push({
      referenceNumber: toString(referenceNumber),
      dateOfBirth: dayjs(dob, dateFormat).format(API_DATE_FORMAT),
      age: toNumber(age),
      plans: processRecommendation(paxRecommendation, params, paramOptions),
      tripBookingDate,
      ...(has(mq, referenceNumber) && { questionnaires: [get(mq, referenceNumber)] })
    } as Pax);
    payload.dependents.push(...travellersToDependents(input.dependentsChanges, referenceNumber, dateFormat));
  } else {
    delete payload.dependents;
    forEach(primaryTravellers, traveller => {
      const { referenceNumber, dob, age, tripCost, tripBookingDate } = traveller;
      const paxRecommendation = find(recommendation, {
        referenceNumber: toString(referenceNumber)
      });
      paramOptions = {
        organizationCode,
        organizationCategory,
        isMaintenance: true,
        tripCost,
        referenceNumber: toNumber(referenceNumber)
      };
      return payload.paxes.push({
        referenceNumber: toString(referenceNumber),
        dateOfBirth: dob,
        age: toNumber(age),
        plans: processRecommendation(paxRecommendation, params, paramOptions),
        tripBookingDate,
        ...(has(mq, referenceNumber) && { questionnaires: [get(mq, referenceNumber)] })
      } as Pax);
    });
  }
  return payload;
};

export const prepareQuote = (store: ReduxStore): QuoteRequestPayload => {
  const { quoteForm, tms, config, userProfile, recommendation, medQuestionnaire, travellersPlan } = store;
  const labels = getLabels(config, tms, 'translationConfig');
  const recommendationData = map(quoteForm.form.travellers, tr => {
    const recData = get(recommendation, `${tr.referenceNumber}.recommendationData`);
    if (isUndefined(recData)) {
      console.info(`[prepareQuote] Missing recommendation data for traveller ${tr.referenceNumber}`);
    }
    return recData;
  });
  return mapQuoteFormToPayload(
    quoteForm.form,
    recommendationData,
    {
      dateFormat: getConfigDateFormat(labels),
      language: toUpper(config.locale),
      organizationCode: userProfile.organizationCode,
      organizationCategory: userProfile.organizationCategory,
      //TODO needed to be synced with entitlement
      agentKey: API_KEYS.STUB_AGENT_KEY,
      useDestinationPricing: checkFeatureFlag(userProfile?.featureFlags, FEATURE_FLAGS.DESTINATION_PRICING)
    },
    {
      mq: medQuestionnaire,
      params: get(travellersPlan, 'tripParams', [])
    }
  );
};

const prepareMQforPolicyInfo = policyInfo => {
  const endorsementPaxes = get(policyInfo, 'selectedPolicies');
  return reduce(
    endorsementPaxes,
    (acc, policy) => {
      const pax = first(get(policy, 'paxes'));
      const referenceNumber = get(pax, 'referenceNumber') as string;
      const questionnaires = get(pax, 'questionnaires');
      const mq = find(questionnaires, questionnaire => get(questionnaire, 'type') === API_KEYS.MEDICAL);
      if (mq) {
        return { ...acc, [referenceNumber]: mq };
      }
      return acc;
    },
    {} as Questionnaire
  );
};

export const prepareQuoteForPolicyInfo = (store: ReduxStore): QuoteRequestPayload => {
  const { policyInfo, tms, config, recommendation, userProfile } = store;
  const labels = getLabels(config, tms);
  const { activeAccordion, selectedPolicies } = policyInfo;
  const organizationCode = get(first(selectedPolicies), 'organizationCode');
  const organizationCategory = userProfile.organizationCategory;

  return mapPolicyInfoToQuotePayload(
    policyInfo,
    map(selectedPolicies, pl => get(recommendation, `${first(pl.paxes).referenceNumber}.recommendationData`)),
    {
      dateFormat: labels.endorse_policy.rider.dateFormat,
      language: toUpper(config.locale),
      organizationCode,
      organizationCategory,
      //TODO needed to be synced with entitlement
      agentKey: API_KEYS.STUB_AGENT_KEY,
      useDestinationPricing: checkFeatureFlag(userProfile?.featureFlags, FEATURE_FLAGS.DESTINATION_PRICING)
    },
    {
      mq: prepareMQforPolicyInfo(policyInfo),
      // @ts-ignore
      params: [get(selectedPolicies[activeAccordion], `paxes[0].plan`, [])]
    }
  );
};

export const mapBackParams = (quote: QuoteRequestPayload): TravellerParams[] => {
  return reduce(
    quote.paxes,
    (res, { plans, referenceNumber }) => [
      ...res,
      ...map(plans, ({ tripPreference, code }) => ({ paxId: referenceNumber, params: tripPreference, plan: code }))
    ],
    []
  );
};

const VALID_PROVINCE_CODES = ['ON', 'QC', 'NS', 'NB', 'MB', 'BC', 'PE', 'SK', 'AB', 'NL', 'YT', 'NT', 'NU'];

const PROVINCE_LABEL_TO_CODE: Record<string, string> = {
  ontario: 'ON',
  québec: 'QC',
  quebec: 'QC',
  'nova scotia': 'NS',
  'new brunswick': 'NB',
  manitoba: 'MB',
  'british columbia': 'BC',
  'prince edward island': 'PE',
  saskatchewan: 'SK',
  alberta: 'AB',
  'newfoundland and labrador': 'NL',
  yukon: 'YT',
  'northwest territories': 'NT',
  nunavut: 'NU'
};

const normalizeProvinceCode = (province: string | undefined, fallback?: string): string => {
  if (!province) return fallback || '';
  const upper = province.trim().toUpperCase();
  if (VALID_PROVINCE_CODES.includes(upper)) return upper;
  const mapped = PROVINCE_LABEL_TO_CODE[province.trim().toLowerCase()];
  if (mapped) return mapped;
  return fallback || province;
};

const prepareContact = (emailPhone, address, fallbackProvince?: string, fallbackEmailPhone?) => ({
  email: emailPhone.email || fallbackEmailPhone?.email || '',
  phone: emailPhone.phone || fallbackEmailPhone?.phone || '',
  address: {
    addressLine1: address.addressLine1 ? address.addressLine1 : '',
    addressLine2: address.addressLine2 ? address.addressLine2 : '',
    city: address.city ? address.city : '',
    postalCode: address.postalCode ? address.postalCode.toUpperCase() : '',
    province: normalizeProvinceCode(address.province, fallbackProvince),
    country: API_KEYS.CANADA
  }
});

const getTravellerDetails = (paxId: number, details: TravellerDetails) =>
  get(details, `formData.traveller-details-${paxId}`);

const getDependentDateOfBirth = (dateOfBirth, dateFormat) => {
  return !isEmpty(dateOfBirth) ? dayjs(dateOfBirth, dateFormat).format(API_DATE_FORMAT) : undefined;
};

// Resolve the quote number that a create-policy payload should carry as
// `originalQuoteNumber`. Extracted so `preparePolicy` stays under Sonar's
// cyclomatic-complexity threshold.
//
// NOTE: intentionally NOT gating on `policyInfo.searchFetched`.
// `QuoteDetails.handleApplyClick` dispatches `resetPolicyInfo()` (POLICY_INFO)
// before navigating to /apply so StepGuard's `if (searchFetched) → pin at
// QUOTE_DETAILS` guard lets the user advance to Contact / Payment. That
// dispatch clears `searchFetched` but leaves `policyInfo.data` intact — so
// `data.records[0].paxes[0].policyStatus === 'QUOTE'` is the durable signal
// that this booking originated from a loaded quote. Gating on `searchFetched`
// here caused `originalQuoteNumber` to silently drop from the create-policy
// payload, persisting the new CMX row with `originalQuoteNumber: null`.
const resolveLoadedQuoteNumber = (policyInfo: ReduxStore['policyInfo'], isQuote: boolean): string | undefined => {
  if (isQuote) {
    return undefined;
  }
  if (get(policyInfo, 'data.records[0].paxes[0].policyStatus') !== 'QUOTE') {
    return undefined;
  }
  return get(policyInfo, 'data.records[0].paxes[0].policyNumber');
};

const prepareFamilyPaxes = (
  store: ReduxStore,
  isQuote: boolean,
  dateFormat: string
): [PolicyPax[], Dependent[]] => {
  const { quoteForm, travellersDetails } = store;
  const pax = first(quoteForm.form.travellers);
  const primaryTraveller = getTravellerDetails(pax.referenceNumber, travellersDetails);
  const primaryPax = mapPolicyPax(isQuote, pax, store, primaryTraveller, pax.referenceNumber, dateFormat);
  
  const dependents = tail(quoteForm.form.travellers);
  const applicationDate = get(quoteForm.form, 'applicationDate')
    ? dayjs(get(quoteForm.form, 'applicationDate', dayjs()), dateFormat).format()
    : dayjs().format(dateFormat);
  
  const dependentPayloads = map(dependents, dependent => {
    const dependentTraveller = getTravellerDetails(dependent.referenceNumber, travellersDetails);
    const emailPhone =
      checkTrue(dependentTraveller.emailPhoneSameAsPrimary) || !primaryPax.payment
        ? primaryTraveller
        : dependentTraveller;
    const address =
      checkTrue(dependentTraveller.addressSameAsPrimary) || !primaryPax.payment
        ? primaryTraveller
        : dependentTraveller;
    return {
      dateOfBirth: getDependentDateOfBirth(dependent.dateOfBirth, dateFormat),
      age: toNumber(dependent.age),
      referenceNumber: toString(dependent.referenceNumber),
      firstName: dependentTraveller.firstName ? dependentTraveller.firstName : QUOTE_BLANK,
      lastName: dependentTraveller.lastName ? dependentTraveller.lastName : QUOTE_BLANK,
      tripCost: toNumber(dependent.tripCost),
      tripBookingDate: dependent.tripBookingDate
        ? dayjs(dependent.tripBookingDate, dependent.format).format()
        : applicationDate,
      contact: prepareContact(emailPhone, address, quoteForm.form.province, primaryTraveller),
      relationship: toUpper(dependentTraveller.relationship) || 'QUOTE'
    };
  });
  
  return [[primaryPax], dependentPayloads];
};

const prepareSinglePaxes = (
  store: ReduxStore,
  isQuote: boolean,
  dateFormat: string
): PolicyPax[] => {
  const { quoteForm, travellersDetails } = store;
  const primary = first(quoteForm.form.travellers);
  const primaryDetails = getTravellerDetails(primary.referenceNumber, travellersDetails);
  return map(quoteForm.form.travellers, traveller =>
    mapPolicyPax(isQuote, traveller, store, primaryDetails, primary.referenceNumber, dateFormat)
  );
};

const buildBasePayload = (
  store: ReduxStore,
  isQuote: boolean,
  dateFormat: string,
  agentList: AgentOption[]
): PolicyRequest => {
  const { quoteForm, userProfile, travellersDetails, travellersPlan } = store;

  const isFamily = checkTrue(quoteForm.form.familyPlans);
  const schoolCity = get(travellersDetails, 'originalFormData.schoolCity1', null);
  const schoolName = get(travellersDetails, 'originalFormData.schoolName1', null);
  const agent = get(travellersDetails, 'originalFormData.agent') as string;
  const agentValue = agentList.filter(ag => ag.label == agent)[0]?.value;
  const loadedQuoteNumber = resolveLoadedQuoteNumber(store.policyInfo, isQuote);
  const appDate = userProfile.claims.includes(POLICY_PERMISSIONS.POLICY_ADMIN)
    ? dayjs(get(quoteForm.form, 'applicationDate', dayjs()), dateFormat).add(6, 'hour').format()
    : dayjs().format();
  const useDestinationPricing = checkFeatureFlag(userProfile?.featureFlags, FEATURE_FLAGS.DESTINATION_PRICING);
  
  return {
    organizationCode: userProfile.organizationCode,
    ...(loadedQuoteNumber && { originalQuoteNumber: loadedQuoteNumber }),
    manulifeEmployeeName: get(quoteForm, 'form.employeeName'),
    manulifeEmployeeRelationship: getManulifeEmployeeRelationShip(get(quoteForm, 'form.employeeType')),
    referenceId: get(quoteForm, 'form.referenceId'),
    travellerType: isFamily ? API_KEYS.FAMILY : API_KEYS.SINGLE,
    travellerCategory:
      quoteForm.form.travellerType ||
      (useDestinationPricing &&
        getTravellerCategoryFromPlanCode(
          get(find(get(travellersPlan, 'products', []), { paxId: first(quoteForm.form.travellers)?.referenceNumber, type: PRODUCT_TYPE.PLAN }), 'code')
        )),
    applicationDate: appDate,
    tripInformation: prepareQuoteTripInformation({ ...quoteForm.form, schoolCity, schoolName }, dateFormat, useDestinationPricing),
    referredBy: agentValue && agentValue !== 'LIA' ? agentValue : ''
  } as PolicyRequest;
};

type AgentOption = { label: string; value: string };

/**
 * Build agent list from user listing data.
 * @param userListing - User listing data containing agent records
 * @returns Array of agent label/value pairs
 */
const buildAgentList = (userListing: ReduxStore['userListing']): AgentOption[] => {
  if (isEmpty(userListing.data)) {
    return [];
  }
  const records = userListing.data?.records ?? [];
  const agents: AgentOption[] = records.map(agent => {
    const agentLabel = `${agent.firstName} ${agent.lastName} (${agent.profileCode})`;
    return { label: agentLabel, value: agent.profileRef ?? '' };
  });
  agents.push({ label: 'Manulife (LIA)', value: 'LIA' });
  return agents;
};

/**
 * Prepare paxes based on plan type (family or single).
 * @param payload - Policy request payload to update
 * @param store - Redux store containing plan data
 * @param isQuote - Whether this is a quote
 * @param dateFormat - Date format for data transformation
 */
const preparePaxesForPolicy = (
  payload: PolicyRequest,
  store: ReduxStore,
  isQuote: boolean,
  dateFormat: string
): void => {
  const isFamily = checkTrue(store.quoteForm.form.familyPlans);
  if (isFamily) {
    const [paxes, dependents] = prepareFamilyPaxes(store, isQuote, dateFormat);
    payload.paxes = paxes;
    payload.dependents = dependents;
  } else {
    payload.paxes = prepareSinglePaxes(store, isQuote, dateFormat);
  }
};

export const preparePolicy = (store: ReduxStore, isQuote: boolean = false): PolicyRequest => {
  const { tms, config, userListing } = store;

  const agentList = buildAgentList(userListing);
  const labels = getLabels(config, tms, 'translationConfig');
  const dateFormat = get(labels, 'configuration.apiDateFormat');
  
  const payload = buildBasePayload(store, isQuote, dateFormat, agentList);
  preparePaxesForPolicy(payload, store, isQuote, dateFormat);

  return payload;
};

export const mapPolicyPax = (
  isQuote: boolean,
  traveller: QuoteFormTraveller,
  store: ReduxStore,
  primaryTravellerDetails: TravellerDetailsPayload,
  primaryTravellerReferenceNumber: number,
  dobFormat?: string
): PolicyPax => {
  const {
    config,
    travellersPlan,
    travellersDetails,
    travellerPayment,
    travellerSelection,
    medQuestionnaire,
    needAnalysis,
    recommendation,
    tms,
    userProfile,
    quoteForm
  } = store;
  const labels = getLabels(config, tms, 'translationConfig');
  const dateFormat = getConfigDateFormat(labels);
  const travellerDetail = getTravellerDetails(traveller.referenceNumber, travellersDetails);
  const travellerPlan = find(travellersPlan.products, {
    paxId: traveller.referenceNumber,
    type: PRODUCT_TYPE.PLAN
  });
  const codeOfTravellersPlan = get(travellerPlan, 'code');
  const { adjustments, ...rest } = mapTripParams(
    chain(travellersPlan.tripParams)
      .find({ paxId: traveller.referenceNumber, plan: codeOfTravellersPlan })
      .get('params', {})
      .value(),
    config.options.organizationCode
  );
  const paymentReferenceNumber = checkTrue(travellerSelection.individualPayment)
    ? traveller.referenceNumber
    : primaryTravellerReferenceNumber;
  const payment = get(travellerPayment, paymentReferenceNumber);
  //if quote - assume email, phone, address same as primary
  const emailPhone =
    checkTrue(travellerDetail.emailPhoneSameAsPrimary) || !payment ? primaryTravellerDetails : travellerDetail;
  const address =
    checkTrue(travellerDetail.addressSameAsPrimary) || !payment ? primaryTravellerDetails : travellerDetail;

  const cardHolderName = get(travellerPayment, `cardHolderName[cardHolderName${paymentReferenceNumber}]`);
  const dateOfBirth = get(traveller, 'dateOfBirth', get(travellerPlan, 'dateOfBirth'));
  const questionnaires = [];

  //check if isMQneeded for that plan, if true, MQ answers is empty
  const planList = get(recommendation[traveller.referenceNumber], 'recommendationData.plans');
  has(medQuestionnaire, traveller.referenceNumber) &&
    some(planList, plan => get(plan, 'code') === codeOfTravellersPlan && get(plan, 'isMQNeeded')) &&
    questionnaires.push(processMQ(medQuestionnaire[traveller.referenceNumber]));
  has(needAnalysis, traveller.referenceNumber) &&
    questionnaires.push(processNA(needAnalysis, traveller.referenceNumber));

  const isCreditCard = eq(get(travellerPayment, `${paymentReferenceNumber}.paymentMethod`), PAYMENT_METHOD.CREDIT_CARD);
  const isLIAUser = userProfile.userType === AUTH_HEADERS.LAN_ID;
  const applicationDate = get(quoteForm.form, 'applicationDate')
    ? dayjs(get(quoteForm.form, 'applicationDate', dayjs()), dateFormat).format()
    : dayjs().format(dateFormat);

  let bill96Consent;
  if (isQuote && toUpper(config.locale) === 'EN' && address.province === 'QC') {
    bill96Consent = true;
  } else if (address.province === 'QC' && toUpper(travellerDetail.language) === 'EN') {
    bill96Consent = get(travellerDetail, 'bill96Consent');
  } else {
    bill96Consent = null;
  }

  return {
    age: toNumber(traveller.age),
    tripBookingDate: traveller.tripBookingDate
      ? dayjs(traveller.tripBookingDate, dateFormat).format()
      : applicationDate,
    dateOfBirth: dayjs(dateOfBirth, dobFormat).format(API_DATE_FORMAT),
    referenceNumber: toString(traveller.referenceNumber),
    // @ts-ignore
    firstName: travellerDetail.firstName
      ? travellerDetail.firstName
      : `${QUOTE_BLANK}${primaryTravellerDetails.firstName}`, //for anyone but primary
    // @ts-ignore
    lastName: travellerDetail.lastName ? travellerDetail.lastName : QUOTE_BLANK,
    plan: {
      code: codeOfTravellersPlan,
      adjustments,
      eligibilityConsent: true,
      tripPreference: {
        tripCost: toNumber(traveller.tripCost || 0),
        ...rest
      },
      optionalBenefits: chain(travellersPlan.products)
        .filter({ paxId: traveller.referenceNumber, type: PRODUCT_TYPE.OPTIONAL_BENEFIT })
        .map(trPlan => ({ code: trPlan.code }))
        .value()
    },
    questionnaires,
    ...(payment &&
      !isQuote && {
        payment: {
          amount: Number(
            chain(travellersPlan.products)
              .filter({ paxId: traveller.referenceNumber })
              .reduce(
                (res: BigNumber, product) => add(res, bignumber(product.total)) as BigNumber,
                bignumber(0) as BigNumber
              )
              .value()
          ) as number,
          type: API_KEYS.CHARGE,
          method:
            payment.paymentMethod === PAYMENT_METHOD.CREDIT_CARD
              ? PAYMENT_METHOD_MAPPING[payment.ccType]
              : payment.paymentMethod,
          ...(isCreditCard && {
            details: {
              cardOwnerReferenceNumber: paymentReferenceNumber,
              dataKey: payment.token,
              cardHolderName,
              ...(isLIAUser
                ? {
                    paymentType: PAYMENT_TYPE.TOKEN
                  }
                : { expDate: payment.expiryDate, cvd: payment.cvv, paymentType: PAYMENT_TYPE.CARD })
            }
          })
        }
      }),
    contact: prepareContact(emailPhone, address, quoteForm.form.province, primaryTravellerDetails),
    lang: toUpper(config.locale),
    ...additionalInfo(travellerDetail),
    bill96Consent
  };
};
